import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Check, ChevronsUpDown, Search, Loader2 } from 'lucide-react';
import { cn } from '../lib/utils';
import { Button } from './ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from './ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';

/**
 * Searchable Customer Select Component
 * Optimized for large datasets (5000+ customers)
 * Features:
 * - Debounced search
 * - Virtual scrolling via limited display
 * - Keyboard navigation
 */
export function CustomerSearchSelect({ 
  customers = [], 
  value, 
  onValueChange, 
  placeholder = "Müşteri ara...",
  disabled = false,
  className = ""
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Filter customers based on search
  const filteredCustomers = useMemo(() => {
    if (!debouncedSearch) {
      // Show first 100 when no search
      return customers.slice(0, 100);
    }
    
    const searchLower = debouncedSearch.toLowerCase();
    return customers
      .filter(c => {
        const name = (c.name || '').toLowerCase();
        const id = (c.customer_id || '').toLowerCase();
        const province = (c.province || '').toLowerCase();
        const city = (c.city || '').toLowerCase();
        const safeId = (c.safe_tourism_id || '').toLowerCase();
        
        return name.includes(searchLower) || 
               id.includes(searchLower) ||
               province.includes(searchLower) ||
               city.includes(searchLower) ||
               safeId.includes(searchLower);
      })
      .slice(0, 100); // Limit results for performance
  }, [customers, debouncedSearch]);

  // Find selected customer
  const selectedCustomer = useMemo(() => {
    return customers.find(c => c.customer_id === value);
  }, [customers, value]);

  const handleSelect = useCallback((customerId) => {
    onValueChange(customerId === value ? '' : customerId);
    setOpen(false);
    setSearch('');
  }, [value, onValueChange]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            "w-full justify-between font-normal",
            !value && "text-muted-foreground",
            className
          )}
          data-testid="customer-search-select"
        >
          {selectedCustomer ? (
            <span className="truncate">
              {selectedCustomer.name}
              {selectedCustomer.audit_phase && (
                <span className="ml-2 text-xs text-emerald-600">
                  ({selectedCustomer.audit_phase}. Aşama)
                </span>
              )}
            </span>
          ) : (
            <span>{placeholder}</span>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[400px] p-0" align="start">
        <Command shouldFilter={false}>
          <div className="flex items-center border-b px-3">
            <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
            <input
              placeholder="Müşteri adı, il, ilçe veya ID ile ara..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="flex h-10 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
            />
          </div>
          <CommandList className="max-h-[300px] overflow-y-auto">
            {filteredCustomers.length === 0 ? (
              <CommandEmpty className="py-6 text-center text-sm">
                {debouncedSearch ? (
                  <span>"{debouncedSearch}" için sonuç bulunamadı</span>
                ) : (
                  <span>Müşteri bulunamadı</span>
                )}
              </CommandEmpty>
            ) : (
              <CommandGroup>
                {filteredCustomers.map((customer) => (
                  <CommandItem
                    key={customer.customer_id}
                    value={customer.customer_id}
                    onSelect={() => handleSelect(customer.customer_id)}
                    className="cursor-pointer"
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        value === customer.customer_id ? "opacity-100" : "opacity-0"
                      )}
                    />
                    <div className="flex flex-col flex-1 min-w-0">
                      <span className="font-medium truncate">{customer.name}</span>
                      <span className="text-xs text-muted-foreground truncate">
                        {customer.province && customer.city 
                          ? `${customer.province} / ${customer.city}`
                          : customer.province || customer.city || ''}
                        {customer.safe_tourism_id && ` • ${customer.safe_tourism_id}`}
                      </span>
                    </div>
                    {customer.audit_phase && (
                      <span className={cn(
                        "ml-2 text-xs px-2 py-0.5 rounded-full",
                        customer.audit_phase === '2' || customer.audit_phase === 2
                          ? "bg-blue-100 text-blue-700"
                          : "bg-emerald-100 text-emerald-700"
                      )}>
                        {customer.audit_phase}. Aşama
                      </span>
                    )}
                  </CommandItem>
                ))}
                {filteredCustomers.length >= 100 && (
                  <div className="py-2 px-3 text-xs text-center text-muted-foreground border-t">
                    İlk 100 sonuç gösteriliyor. Daha fazla sonuç için aramayı daraltın.
                  </div>
                )}
              </CommandGroup>
            )}
          </CommandList>
          <div className="border-t px-3 py-2 text-xs text-muted-foreground">
            Toplam {customers.length} müşteri • {filteredCustomers.length} sonuç
          </div>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default CustomerSearchSelect;
